import { create } from "zustand";
import { StoreTable } from "@/models/tableModel";
import {
  fetchTables,
  createTable as createTableService,
  updateTable as updateTableService,
  deleteTable as deleteTableService,
} from "@/services/tables";
import { supabase } from "@/integrations/supabase/client";
import { pushNotificationService } from "@/services/notifications/pushNotificationService";

interface TableState {
  tables: StoreTable[];
  isLoading: boolean;
  error: string | null;
  selectedStoreId?: string;
  setSelectedStoreId: (id?: string) => void;
  updateTableScans: (tableId: string) => Promise<void>;
  fetchTables: (storeId?: string) => Promise<void>;
  createTable: (
    table: Omit<StoreTable, "id" | "created_at" | "updated_at">
  ) => Promise<StoreTable>;
  updateTable: (
    id: string,
    updates: Partial<StoreTable>
  ) => Promise<StoreTable>;
  deleteTable: (id: string) => Promise<boolean>;
  assignStaffToTable: (
    tableId: string,
    staffId: string | null
  ) => Promise<void>;
  setupRealtimeSubscription: (storeId: string) => () => void;
}

export const useTableStore = create<TableState>((set, get) => ({
  tables: [],
  isLoading: false,
  error: null,
  selectedStoreId: undefined,

  setSelectedStoreId: (id?: string) => {
    set({ selectedStoreId: id });
  },

  updateTableScans: async (tableId: string) => {
    set({ isLoading: true });
    try {
      const { data, error } = await supabase
        .from("store_tables")
        .select(
          `
          *,
          staff:assigned_staff_id (
            id,
            first_name,
            last_name,
            role
          )
        `
        )
        .eq("id", tableId)
        .single();

      if (error) throw error;

      const updatedTable = {
        ...data,
        staffName: data.staff
          ? `${data.staff.first_name} ${data.staff.last_name}`
          : null,
        staffRole: data.staff?.role || null,
      };
// @ts-expect-error - staff is not typed
      set((state) => ({
        tables: state.tables.map((table) =>
          table.id === tableId ? updatedTable : table
        ),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  fetchTables: async (storeId?: string) => {
    set({ isLoading: true });
    try {
      const tables = await fetchTables(storeId || get().selectedStoreId || "");
      set({ tables, isLoading: false, error: null });
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
    }
  },

  createTable: async (table) => {
    set({ isLoading: true });
    try {
      const newTable = await createTableService(table);
      set((state) => ({
        tables: [...state.tables, newTable],
        isLoading: false,
        error: null,
      }));
      return newTable;
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
      throw error;
    }
  },

  updateTable: async (id, updates) => {
    set({ isLoading: true });
    try {
      const updatedTable = await updateTableService(id, updates);
      set((state) => ({
        tables: state.tables.map((table) =>
          table.id === id ? updatedTable : table
        ),
        isLoading: false,
        error: null,
      }));
      return updatedTable;
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
      throw error;
    }
  },

  deleteTable: async (id) => {
    set({ isLoading: true });
    try {
      await deleteTableService(id);
      set((state) => ({
        tables: state.tables.filter((table) => table.id !== id),
        isLoading: false,
        error: null,
      }));
      return true;
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
      return false;
    }
  },

  assignStaffToTable: async (tableId, staffId) => {
    set({ isLoading: true });
    try {
      // Get current table data to check previous assignment
      const { data: currentTable, error: currentError } = await supabase
        .from("store_tables")
        .select(`
          *,
          staff:assigned_staff_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq("id", tableId)
        .single();

      if (currentError) throw currentError;

      const previousStaffId = currentTable.assigned_staff_id;
      const previousStaffName = currentTable.staff 
        ? `${currentTable.staff.first_name} ${currentTable.staff.last_name}`
        : null;

      // Update the table assignment
      const { data, error } = await supabase
        .from("store_tables")
        .update({ assigned_staff_id: staffId })
        .eq("id", tableId)
        .select(
          `
          *,
          staff:assigned_staff_id (
            id,
            first_name,
            last_name,
            role
          )
        `
        )
        .single();

      if (error) throw error;

      const updatedTable = {
        ...data,
        staffName: data.staff
          ? `${data.staff.first_name} ${data.staff.last_name}`
          : null,
        staffRole: data.staff?.role || null,
      };

      // @ts-expect-error - staff is not typed
      set((state) => ({
        tables: state.tables.map((table) =>
          table.id === tableId ? updatedTable : table
        ),
        isLoading: false,
        error: null,
      }));

      // Send push notifications
      try {
        if (staffId && data.staff) {
          // Table was assigned to staff
          await pushNotificationService.notifyTableAssigned(
            data,
            staffId,
            `${data.staff.first_name} ${data.staff.last_name}`,
            previousStaffId,
            previousStaffName
          );
        } else if (previousStaffId && previousStaffName) {
          // Table was unassigned from staff
          await pushNotificationService.notifyTableUnassigned(
            data,
            previousStaffId,
            previousStaffName
          );
        }
      } catch (notificationError) {
        console.error('Error sending table assignment notification:', notificationError);
        // Don't fail the assignment if notifications fail
      }
    } catch (error) {
      set({
        isLoading: false,
        error:
          error instanceof Error ? error.message : "An unknown error occurred",
      });
      throw error;
    }
  },

  setupRealtimeSubscription: (storeId: string) => {
    const channel = supabase
      .channel("store_tables_channel")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "store_tables",
          filter: `store_id=eq.${storeId}`,
        },
        async (payload) => {
          // Refresh the tables list when any table is updated
          await get().fetchTables(storeId);
        }
      )
      .subscribe();

    // Return cleanup function
    return () => {
      supabase.removeChannel(channel);
    };
  },
}));

// import { create } from "zustand";
// import { StoreTable } from "@/models/tableModel";
// import {
//   fetchTables,
//   createTable as createTableService,
//   updateTable as updateTableService,
//   deleteTable as deleteTableService,
// } from "@/services/tables";
// import { useStoreManagementStore } from "./storeManagementStore";
// import { toast } from "@/hooks/use-toast";

// interface TableState {
//   tables: StoreTable[];
//   isLoading: boolean;
//   error: string | null;
//   fetchStoreTables: () => Promise<void>;
//   addTable: (
//     table: Omit<StoreTable, "id" | "created_at" | "updated_at">
//   ) => Promise<StoreTable | null>;
//   updateTable: (
//     id: string,
//     updates: Partial<StoreTable>
//   ) => Promise<StoreTable | null>;
//   deleteTable: (id: string) => Promise<boolean>;
//   reset: () => void;
// }

// export const useTableStore = create<TableState>((set, get) => ({
//   tables: [],
//   isLoading: false,
//   error: null,
//   fetchStoreTables: async () => {
//     const selectedStoreId = useStoreManagementStore.getState().selectedStoreId;
//     if (!selectedStoreId) {
//       set({ tables: [], isLoading: false, error: "No store selected" });
//       return;
//     }

//     set({ isLoading: true, error: null });

//     try {
//       const tables = await fetchTables(selectedStoreId);
//       set({ tables: tables, isLoading: false, error: null });
//     } catch (error) {
//       console.error("Error fetching tables:", error);
//       set({
//         tables: [],
//         isLoading: false,
//         error: "Failed to load tables",
//       });
//     }
//   },

//   addTable: async (table) => {
//     try {
//       const tableWithDefaults = {
//         ...table,
//         daily_scans: 0,
//         total_scans: 0,
//       };

//       const newTable = await addTable(tableWithDefaults);
//       if (newTable) {
//         set((state) => ({ tables: [...state.tables, newTable] }));
//         toast({
//           title: "Success",
//           description: "Table added successfully",
//         });
//       }
//       return newTable;
//     } catch (error) {
//       console.error("Error adding table:", error);
//       toast({
//         title: "Error",
//         description: "Failed to add table",
//         variant: "destructive",
//       });
//       return null;
//     }
//   },

//   updateTable: async (id, updates) => {
//     try {
//       const updatedTable = await updateTable(id, updates);
//       if (updatedTable) {
//         set((state) => ({
//           tables: state.tables.map((table) =>
//             table.id === id ? updatedTable : table
//           ),
//         }));
//         toast({
//           title: "Success",
//           description: "Table updated successfully",
//         });
//       }
//       return updatedTable;
//     } catch (error) {
//       console.error("Error updating table:", error);
//       toast({
//         title: "Error",
//         description: "Failed to update table",
//         variant: "destructive",
//       });
//       return null;
//     }
//   },

//   deleteTable: async (id) => {
//     try {
//       const success = await deleteTable(id);
//       if (success) {
//         set((state) => ({
//           tables: state.tables.filter((table) => table.id !== id),
//         }));
//         toast({
//           title: "Success",
//           description: "Table deleted successfully",
//         });
//       }
//       return success;
//     } catch (error) {
//       console.error("Error deleting table:", error);
//       toast({
//         title: "Error",
//         description: "Failed to delete table",
//         variant: "destructive",
//       });
//       return false;
//     }
//   },

//   reset: () => {
//     set({ tables: [], isLoading: false, error: null });
//   },
// }));

// useStoreManagementStore.subscribe((state) => {
//   const selectedStoreId = state.selectedStoreId;
//   if (selectedStoreId) {
//     useTableStore.getState().fetchStoreTables();
//   } else {
//     useTableStore.getState().reset();
//   }
// });
